import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getAvailableUnits, addShowing, updateShowing, deleteShowing } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, Plus, Pencil, Trash2, Calendar, Clock, Phone, Mail, MessageSquare, Save } from 'lucide-react';
import { toast } from 'sonner';
import { UnitOverridePicker } from './UnitOverridePicker';
import { StartEndDateFields } from '@/components/common/DatePickers';

// These save instantly on their own, so they never count as unsaved changes.
const AUTOSAVED_KEYS = ['emailed_dates', 'called_dates', 'texted_dates', 'last_contact_date', 'showings'];
const omitAutosaved = (f) => Object.fromEntries(Object.entries(f || {}).filter(([k]) => !AUTOSAVED_KEYS.includes(k)));

function ContactTrackingSection({ form, setForm, viewMode, onAutosave }) {
  const today = new Date().toISOString().split('T')[0];
  const channels = [
    { id: 'emailed_dates', label: 'Emails', Icon: Mail, color: 'text-sky-700 bg-sky-50' },
    { id: 'called_dates', label: 'Calls', Icon: Phone, color: 'text-emerald-700 bg-emerald-50' },
    { id: 'texted_dates', label: 'Texts', Icon: MessageSquare, color: 'text-violet-700 bg-violet-50' },
  ];
  // Recompute last_contact_date as the max date across all three channels.
  const recomputeLast = (next) => {
    const all = [
      ...(next.emailed_dates || []),
      ...(next.called_dates || []),
      ...(next.texted_dates || []),
    ].filter(Boolean);
    return all.sort().slice(-1)[0] || '';
  };
  // Whenever a contact-log entry is added / edited / removed we both update
  // local form state AND fire `onAutosave` so the Quick Leads list can
  // persist the change immediately. This means contact-log edits work even
  // in the read-only "view" presentation of the dialog — no Edit click first.
  const applyChange = (id, nextArray) => {
    setForm((f) => {
      const next = { ...f, [id]: nextArray };
      next.last_contact_date = recomputeLast(next);
      if (onAutosave) onAutosave({ [id]: nextArray, last_contact_date: next.last_contact_date });
      return next;
    });
  };
  const addToday = (id) => applyChange(id, [...((form[id]) || []), today]);
  const updateAt = (id, idx, value) => {
    const cur = form[id] || [];
    applyChange(id, cur.map((d, i) => (i === idx ? value : d)));
  };
  const removeAt = (id, idx) => {
    const cur = form[id] || [];
    applyChange(id, cur.filter((_, i) => i !== idx));
  };
  // Contact-log edits are allowed even when the rest of the dialog is in
  // view mode (they're treated as quick actions), but only when the parent
  // can autosave them — otherwise they'd be silently lost when the dialog
  // closes. For current-leads (no autosave wired up) we still respect the
  // viewMode gate.
  const editable = !viewMode || !!onAutosave;
  return (
    <div className="space-y-2">
      <Label className="text-sm">Contact Log</Label>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {channels.map(({ id, label, Icon, color }) => {
          const original = form[id] || [];
          const indexed = original.map((d, idx) => ({ d, idx }));
          // Sort visually by recency without breaking the underlying index.
          const sorted = [...indexed].sort((a, b) => (b.d || '').localeCompare(a.d || ''));
          return (
            <div key={id} className={`rounded border p-2 ${color}`}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5 text-xs font-semibold">
                  <Icon className="h-3 w-3" /> {label}
                  <span className="tabular-nums">·{original.length}</span>
                </div>
                {editable && (
                  <Button
                    size="sm" variant="ghost"
                    className="h-6 text-[10px] px-1.5"
                    onClick={() => addToday(id)}
                    data-testid={`contact-log-${id}`}
                    title="Add today's date — edit inline if needed"
                  >
                    <Plus className="h-2.5 w-2.5 mr-0.5" /> Add
                  </Button>
                )}
              </div>
              <div className="space-y-1 max-h-28 overflow-y-auto">
                {sorted.length === 0 && <p className="text-[11px] text-slate-500 italic">No entries</p>}
                {sorted.map(({ d, idx }) => (
                  <div key={idx} className="flex items-center gap-1">
                    {editable ? (
                      <Input
                        type="date"
                        value={d || ''}
                        onChange={(e) => updateAt(id, idx, e.target.value)}
                        className="h-6 text-[11px] px-1.5 py-0 tabular-nums bg-white/70 border-white/80 focus-visible:ring-1"
                        data-testid={`contact-date-${id}-${idx}`}
                      />
                    ) : (
                      <span className="text-[11px] tabular-nums flex-1">{d}</span>
                    )}
                    {editable && (
                      <button
                        type="button"
                        onClick={() => removeAt(id, idx)}
                        className="opacity-60 hover:opacity-100 hover:text-destructive p-0.5"
                        data-testid={`contact-remove-${id}-${idx}`}
                        title="Remove this entry"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {form.last_contact_date && (
        <p className="text-[10px] text-slate-500">Last contact: <span className="font-medium tabular-nums">{form.last_contact_date}</span></p>
      )}
    </div>
  );
}

function ShowingsSection({ leadId, showings = [], onShowingsUpdated, onDraftChange, draftSaveRef }) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showingForm, setShowingForm] = useState({ date: '', time: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setShowingForm({ date: '', time: '', notes: '' });
    setAdding(false);
    setEditingId(null);
  };

  // ── Draft tracking ────────────────────────────────────────────
  // We notify the parent about the draft using PRIMITIVES only
  // (has/mode) via `onDraftChange`, and stash the actual save closure
  // on a ref (`draftSaveRef`). Refs don't trigger re-renders, so we
  // avoid the infinite loop that used to happen when we passed a
  // freshly-allocated `{ form, save }` object into parent state on
  // every render.
  const doAddOrUpdate = async (form) => {
    if (!form.date || !form.time) throw new Error('date & time required');
    if (editingId) {
      const updated = await updateShowing(leadId, editingId, form);
      onShowingsUpdated(updated.showings || []);
    } else {
      const updated = await addShowing(leadId, form);
      onShowingsUpdated(updated.showings || []);
    }
  };

  const hasDraft = (adding || !!editingId) && !!showingForm.date && !!showingForm.time;
  const draftMode = editingId ? 'edit' : 'add';

  // Keep the save closure fresh without re-rendering the parent.
  if (draftSaveRef) {
    draftSaveRef.current = hasDraft
      ? async () => { await doAddOrUpdate(showingForm); resetForm(); }
      : null;
  }

  useEffect(() => {
    if (!onDraftChange) return;
    onDraftChange(hasDraft ? { mode: draftMode } : null);
  }, [hasDraft, draftMode, onDraftChange]);

  const handleAdd = async () => {
    if (!showingForm.date || !showingForm.time) {
      toast.error('Date and time are required');
      return;
    }
    setSaving(true);
    try {
      const updated = await addShowing(leadId, showingForm);
      onShowingsUpdated(updated.showings || []);
      resetForm();
      toast.success('Showing added');
    } catch {
      toast.error('Failed to add showing');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!showingForm.date || !showingForm.time) {
      toast.error('Date and time are required');
      return;
    }
    setSaving(true);
    try {
      const updated = await updateShowing(leadId, editingId, showingForm);
      onShowingsUpdated(updated.showings || []);
      resetForm();
      toast.success('Showing updated');
    } catch {
      toast.error('Failed to update showing');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (showingId) => {
    try {
      const updated = await deleteShowing(leadId, showingId);
      onShowingsUpdated(updated.showings || []);
      toast.success('Showing removed');
    } catch {
      toast.error('Failed to delete showing');
    }
  };

  const startEdit = (showing) => {
    setEditingId(showing.id);
    setShowingForm({ date: showing.date, time: showing.time, notes: showing.notes || '' });
    setAdding(false);
  };

  const fmtDate = (d) => {
    try {
      const dt = new Date(d + 'T00:00:00');
      return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    } catch { return d; }
  };

  const fmtTime = (t) => {
    try {
      const [h, m] = t.split(':');
      const hr = parseInt(h);
      const ampm = hr >= 12 ? 'PM' : 'AM';
      return `${hr > 12 ? hr - 12 : hr || 12}:${m} ${ampm}`;
    } catch { return t; }
  };

  const sorted = [...showings].sort((a, b) => {
    const cmp = (a.date || '').localeCompare(b.date || '');
    return cmp !== 0 ? cmp : (a.time || '').localeCompare(b.time || '');
  });

  return (
    <div className="space-y-3" data-testid="showings-section">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Showings ({showings.length})</Label>
        {!adding && !editingId && (
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => { setAdding(true); setEditingId(null); setShowingForm({ date: '', time: '', notes: '' }); }} data-testid="add-showing-button">
            <Plus className="h-3 w-3" /> Add Showing
          </Button>
        )}
      </div>

      {/* Existing showings */}
      {sorted.length > 0 && (
        <div className="space-y-1.5">
          {sorted.map((s) => (
            <div key={s.id} className="flex items-center justify-between px-3 py-2 rounded-lg border bg-gray-50/50 group" data-testid={`showing-${s.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex items-center gap-1.5 text-[13px] text-gray-700">
                  <Calendar className="h-3.5 w-3.5 text-gray-400" />
                  <span className="font-medium">{fmtDate(s.date)}</span>
                </div>
                <div className="flex items-center gap-1.5 text-[13px] text-gray-500">
                  <Clock className="h-3.5 w-3.5 text-gray-400" />
                  <span>{fmtTime(s.time)}</span>
                </div>
                {s.notes && <span className="text-[11px] text-gray-400 truncate max-w-[150px]">{s.notes}</span>}
              </div>
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => startEdit(s)} data-testid={`edit-showing-${s.id}`}>
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => handleDelete(s.id)} data-testid={`delete-showing-${s.id}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit form */}
      {(adding || editingId) && (
        <div className="p-3 rounded-lg border border-teal-200 bg-teal-50/30 space-y-3" data-testid="showing-form">
          <p className="text-xs font-medium text-teal-800">{editingId ? 'Edit Showing' : 'New Showing'}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Date *</Label>
              <Input type="date" value={showingForm.date} onChange={e => setShowingForm(f => ({ ...f, date: e.target.value }))} data-testid="showing-date-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Time *</Label>
              <Input type="time" value={showingForm.time} onChange={e => setShowingForm(f => ({ ...f, time: e.target.value }))} data-testid="showing-time-input" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Notes</Label>
            <Input value={showingForm.notes} onChange={e => setShowingForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes" data-testid="showing-notes-input" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs" onClick={editingId ? handleUpdate : handleAdd} disabled={saving} data-testid="showing-save-button">
              {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={resetForm}>Cancel</Button>
          </div>
        </div>
      )}

      {showings.length === 0 && !adding && (
        <p className="text-xs text-muted-foreground">No showings scheduled</p>
      )}
    </div>
  );
}

export function LeadFormDialog({ open, onOpenChange, editing, form, setForm, onSave, onSaveAsUnassigned, saving, onLeadUpdated, onContactLogAutosave, defaultViewMode = false, kind = 'current' }) {
  const [availableUnits, setAvailableUnits] = useState([]);
  const [noUnitsAvailable, setNoUnitsAvailable] = useState(false);
  // View mode locks every input + hides Save buttons; Edit button in the
  // header flips us back to editable. Reset to view mode whenever a new lead
  // is opened so clicking a row always lands on the read-only summary first.
  const [viewMode, setViewMode] = useState(defaultViewMode);
  // ── Dirty-state tracking ─────────────────────────────────────────
  // Snapshot the form the first time the dialog opens for a given lead
  // and compare on every render. When the user tries to close (X, ESC,
  // outside click, or Cancel button), we block the close and ask them
  // to save/discard first. `showingDraft` is populated by the
  // ShowingsSection whenever a pending Add/Edit showing has both date
  // and time filled — so we can surface a matching confirmation.
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [showingDraft, setShowingDraft] = useState(null);
  const [pendingClose, setPendingClose] = useState(false);
  const draftSaveRef = useRef(null);
  useEffect(() => {
    if (open) {
      setViewMode(defaultViewMode);
      // JSON snapshot is fine — form only holds primitives + string arrays.
      setInitialSnapshot(JSON.stringify(editing ? omitAutosaved(form) : form));
      setShowingDraft(null);
      setPendingClose(false);
    }
  }, [open, defaultViewMode, editing?.id]);

  const isDirty = useMemo(() => {
    if (!open || initialSnapshot == null) return false;
    try { return JSON.stringify(editing ? omitAutosaved(form) : form) !== initialSnapshot; } catch { return false; }
  }, [form, initialSnapshot, open, editing]);

  const fetchAvailableUnits = useCallback(async () => {
    try {
      const units = await getAvailableUnits(form.desired_start_date, form.desired_end_date);
      setAvailableUnits(units);
      setNoUnitsAvailable(form.desired_start_date && form.desired_end_date && units.length === 0);
    } catch {
      toast.error('Failed to fetch available units');
    }
  }, [form.desired_start_date, form.desired_end_date]);

  useEffect(() => {
    if (open) fetchAvailableUnits();
  }, [open, fetchAvailableUnits]);

  const toggleUnit = (unitId) => {
    if (viewMode) return;
    const current = form.potential_unit_ids;
    if (current.includes(unitId)) {
      setForm(f => ({ ...f, potential_unit_ids: current.filter(id => id !== unitId) }));
    } else {
      setForm(f => ({ ...f, potential_unit_ids: [...current, unitId] }));
    }
  };

  const handleShowingsUpdated = (newShowings) => {
    setForm(f => ({ ...f, showings: newShowings }));
    if (onLeadUpdated) onLeadUpdated();
  };

  // Effective save routine used by both the header quick-save and the
  // "Save & Close" branch in the confirmation dialog. `is_unassigned`
  // and Save buttons share the same code path as the manual footer clicks.
  const runSave = async () => {
    if (form.is_unassigned && onSaveAsUnassigned) {
      await onSaveAsUnassigned();
    } else {
      await onSave();
    }
  };

  // Requested-close handler intercepts every "user wants to close" trigger
  // (X icon, ESC, outside click, Cancel/Close footer buttons). If the form
  // is dirty or a showing draft is pending, we surface the confirmation
  // dialog instead of closing.
  const requestClose = () => {
    if (saving) return;
    if (showingDraft) { setPendingClose(true); return; }
    if (isDirty && !viewMode) { setPendingClose(true); return; }
    onOpenChange(false);
  };

  const confirmDiscard = () => {
    setPendingClose(false);
    setShowingDraft(null);
    onOpenChange(false);
  };

  const confirmSaveAndClose = async () => {
    try {
      // Order matters: save the lead FIRST (PUT /leads/{id} carries the
      // current showings array). If we posted the showing before the PUT,
      // the parent's stale `form.showings` would clobber the newly-added
      // showing since setForm is async. By running the lead save first,
      // the follow-up POST /leads/{id}/showings is purely additive and
      // survives on the backend regardless of the parent unmounting.
      // Capture the save closure now — parent may unmount ShowingsSection
      // during runSave (setDialogOpen(false) in the parent handler).
      const pendingShowingSave = draftSaveRef.current;
      await runSave();
      if (pendingShowingSave) {
        await pendingShowingSave();
      }
      setPendingClose(false);
      setShowingDraft(null);
      onOpenChange(false);
    } catch (e) {
      // Save handler already toasts errors. Leave the dialog open so the
      // user can inspect what went wrong.
      setPendingClose(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) requestClose(); else onOpenChange(true); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onEscapeKeyDown={(e) => { e.preventDefault(); requestClose(); }} onPointerDownOutside={(e) => { e.preventDefault(); requestClose(); }}>
        <DialogHeader>
          <div className="flex items-center justify-between gap-2 pr-6">
            <div className="flex items-center gap-2">
              <DialogTitle className="font-heading">
                {editing ? (viewMode ? (kind === 'quick' ? 'Quick Lead Details' : 'Lead Details') : (kind === 'quick' ? 'Edit Quick Lead' : 'Edit Lead')) : (kind === 'quick' ? 'Add Quick Lead' : 'Add Lead')}
              </DialogTitle>
              {isDirty && !viewMode && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium" data-testid="lead-dirty-badge">Unsaved</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {editing && viewMode && (
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setViewMode(false)} data-testid="lead-edit-toggle">
                  <Pencil className="h-3 w-3" /> Edit
                </Button>
              )}
              {!viewMode && isDirty && (
                <Button size="sm" className="h-7 text-xs gap-1 bg-teal-700 hover:bg-teal-800" onClick={runSave} disabled={saving} data-testid="lead-header-save">
                  <Save className="h-3 w-3" /> {saving ? 'Saving…' : (editing ? 'Update' : 'Create')}
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>
        <fieldset disabled={viewMode} className="contents">
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} data-testid="lead-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Source</Label>
              <Input value={form.source} onChange={e => setForm(f => ({...f, source: e.target.value}))} placeholder="Where was lead found" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} />
            </div>
            <div className="space-y-2">
              <Label>Preferred Contact Method</Label>
              <Input value={form.preferred_contact_method} onChange={e => setForm(f => ({...f, preferred_contact_method: e.target.value}))} placeholder="e.g. Text, Email, Call" data-testid="lead-contact-method" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StartEndDateFields
              start={form.desired_start_date || ''}
              end={form.desired_end_date || ''}
              onChange={({ start, end }) => setForm(f => ({ ...f, desired_start_date: start, desired_end_date: end }))}
              startLabel="Desired Start Date"
              endLabel="Desired End Date"
              startTestId="lead-desired-start-input"
              endTestId="lead-desired-end-input"
              labelClassName=""
              className="contents"
            />
            <div className="space-y-2">
              <Label>Price Offered</Label>
              <Input type="number" value={form.price_offered} onChange={e => setForm(f => ({...f, price_offered: e.target.value}))} placeholder="$ amount" data-testid="lead-price-offered" />
            </div>
          </div>

          {/* Available units */}
          <div className="space-y-2">
            <Label>Potential Units</Label>
            {noUnitsAvailable && !form.is_unassigned ? (
              <div className="p-4 rounded-lg border border-amber-200 bg-amber-50/50">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">No available units for these dates</p>
                    <p className="text-xs text-amber-700 mt-1">You can save this as an unassigned lead to follow up later.</p>
                    <Button variant="outline" size="sm" className="mt-2 h-7 text-xs border-amber-300 text-amber-900 hover:bg-amber-100"
                      onClick={() => setForm(f => ({...f, is_unassigned: true}))}
                      data-testid="lead-save-unassigned-toggle">
                      <AlertCircle className="h-3 w-3 mr-1" /> Mark as Unassigned
                    </Button>
                  </div>
                </div>
              </div>
            ) : form.is_unassigned ? (
              <div className="p-3 rounded-lg border border-amber-200 bg-amber-50/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                    <span className="text-sm font-medium text-amber-900">Saving as unassigned lead</span>
                  </div>
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setForm(f => ({...f, is_unassigned: false}))}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 p-3 rounded-lg border min-h-[48px]">
                {availableUnits.length === 0 ? (
                  <span className="text-sm text-muted-foreground">
                    {form.desired_start_date && form.desired_end_date
                      ? 'No available units for selected dates'
                      : 'Select dates to see available units'}
                  </span>
                ) : (
                  availableUnits.map(u => (
                    <Badge key={u.id}
                      variant={form.potential_unit_ids.includes(u.id) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleUnit(u.id)}>
                      {u.property_name} - {u.unit_number}
                    </Badge>
                  ))
                )}
              </div>
            )}
            {!form.is_unassigned && (
              <UnitOverridePicker
                selectedIds={form.potential_unit_ids || []}
                availableIds={availableUnits.map(u => u.id)}
                onToggle={toggleUnit}
                startDate={form.desired_start_date}
                endDate={form.desired_end_date}
              />
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Pets</Label>
              <Input value={form.pets} onChange={e => setForm(f => ({...f, pets: e.target.value}))} />
            </div>
            <div className="space-y-2">
              <Label>Parking Request</Label>
              <Input value={form.parking_request} onChange={e => setForm(f => ({...f, parking_request: e.target.value}))} />
            </div>
          </div>
          {kind !== 'quick' && (
          <div className="space-y-2">
            <Label>Lead Strength</Label>
            <Select value={String(form.lead_strength)} onValueChange={v => setForm(f => ({...f, lead_strength: parseInt(v)}))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 - Weak (Red)</SelectItem>
                <SelectItem value="2">2 - Fair (Orange)</SelectItem>
                <SelectItem value="3">3 - Good (Yellow)</SelectItem>
                <SelectItem value="4">4 - Strong (Green)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          )}
          {/* Showings management — only for current leads */}
          {kind !== 'quick' && editing && form.id && (
            <ShowingsSection
              leadId={form.id}
              showings={form.showings || []}
              onShowingsUpdated={handleShowingsUpdated}
              onDraftChange={setShowingDraft}
              draftSaveRef={draftSaveRef}
            />
          )}

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Any additional notes about this lead..." data-testid="lead-notes-input" />
          </div>
        </div>
        </fieldset>

        {/* Contact tracking — rendered OUTSIDE the disabled fieldset so the
            contact-log inputs stay live even when the rest of the dialog is
            in view-mode (HTML disabled state propagates from parent
            fieldsets, so nesting another fieldset doesn't re-enable). */}
        {(kind === 'quick' || form.id) && (
          <div className="px-1 pb-4">
            <ContactTrackingSection
              form={form}
              setForm={setForm}
              viewMode={viewMode}
              onAutosave={onContactLogAutosave}
            />
          </div>
        )}
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={requestClose} data-testid="lead-cancel-button">{viewMode ? 'Close' : 'Cancel'}</Button>
          {!viewMode && (form.is_unassigned ? (
            <Button onClick={onSaveAsUnassigned} disabled={saving} data-testid="lead-save-button">
              {saving ? 'Saving...' : 'Save as Unassigned'}
            </Button>
          ) : (
            <Button onClick={onSave} disabled={saving} data-testid="lead-save-button">
              {saving ? 'Saving...' : (editing ? 'Update' : 'Create')}
            </Button>
          ))}
        </DialogFooter>
      </DialogContent>

      {/* Unsaved-changes / pending-showing confirmation */}
      <AlertDialog open={pendingClose} onOpenChange={setPendingClose}>
        <AlertDialogContent data-testid="lead-close-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {showingDraft ? 'Add showing before closing?' : 'Save changes before closing?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {showingDraft
                ? `You started a ${showingDraft.mode === 'edit' ? 'showing edit' : 'new showing'} that hasn't been added yet. Add it now and save your changes, or discard.`
                : 'You have unsaved changes on this lead. Save them now, or discard to close.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingClose(false)} data-testid="lead-close-keep-editing">Keep editing</AlertDialogCancel>
            <Button variant="outline" onClick={confirmDiscard} data-testid="lead-close-discard">Discard</Button>
            <AlertDialogAction onClick={confirmSaveAndClose} disabled={saving} data-testid="lead-close-save">
              {saving ? 'Saving…' : (showingDraft ? 'Add showing & save' : 'Save & close')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
